import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  followUser,
  unfollowUser,
  isFollowingUser,
  fetchFollowersCount,
  fetchFollowingCount,
} from "../../../services/profileService";
import ForwardRoundedIcon from "@mui/icons-material/ForwardRounded";

const ProfileHeader = ({ userProfile, currentUser,artistProfile, theme }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const isCurrentUser = currentUser?.uid === userProfile?.uid;
  const navigate = useNavigate();

  // Fetch following status, followers count, and following count
  useEffect(() => {
    const fetchData = async () => {
      if (!isCurrentUser && currentUser && userProfile) {
        try {
          const followingStatus = await isFollowingUser(
            currentUser, // ✅ Correctly pass the user object
            userProfile.uid
          );
          setIsFollowing(followingStatus);
        } catch (error) {
          console.error("Error fetching follow status:", error);
        }
      }

      if (userProfile) {
        try {
          const followers = await fetchFollowersCount(userProfile.uid);
          const following = await fetchFollowingCount(userProfile.uid);
          setFollowersCount(followers);
          setFollowingCount(following);
        } catch (error) {
          console.error("Error fetching counts:", error);
        }
      }
    };

    fetchData();
  }, [userProfile, currentUser, isCurrentUser]);

  const handleFollowToggle = async () => {
    // Check if the user is authenticated
    if (!currentUser) {
      // If not authenticated, prompt them to log in
      alert("You need to be logged in to follow users.");
      navigate("/challenge/signin"); // Navigate to the signin page
      return;
    }

    try {
      if (isFollowing) {
        await unfollowUser(currentUser, userProfile);
        setIsFollowing(false);
        setFollowersCount((prev) => prev - 1);
      } else {
        await followUser(currentUser, userProfile);
        setIsFollowing(true);
        setFollowersCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
    }
  };

  const handleClick = async () => {
      navigate("/challenge");
  };

  // Extract the first name from the display name
  const displayName = userProfile ? (userProfile?.displayName || "Anonymous") : artistProfile?.fullName;
  const firstName = displayName?.split(" ")[0];

  return (
    <Box>
      {/* First Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
          gap: 2, // optional spacing
        }}
>
        {/* First Column: User Avatar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
            marginRight: { xs: 0, sm: 2 },
            mb: { xs: 2, sm: 0 }, // Add margin bottom on small screens
          }}
        >
          <Avatar
            src={userProfile ? userProfile?.profileImage : artistProfile?.profilePicture}
            alt={displayName}
            sx={{ width: 80, height: 80, margin: "15px 15px 15px 0" }}
          />
        </Box>

        {/* Second Column: User Display Name and Stats */}
        <Box
          sx={{
            flex: 1,
            textAlign: {
            xs: "center",
            sm: "center",
            md: "left",
      },
      fontFamily: "basic-sans",
      width: "100%",
    }}
  >
    <Typography
      variant="h4"
      component="h2"
      gutterBottom
      sx={{ fontFamily: "basic-sans", fontWeight: "700" }}
    >
      {displayName}
    </Typography>
    <Typography
      variant="body1"
      color={theme.colors.darkGray}
      sx={{ fontFamily: "basic-sans" }}
    >
      Following: {followingCount} Followers: {followersCount}
    </Typography>

    {/* Stack Edit Profile and View Contributions links on mobile */}
    {isCurrentUser && (
      <Box
        sx={{
          mt: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <MuiLink
          component={Link}
          to="/challenge/profile/edit"
          sx={{
            color: theme.colors.amber,
            fontFamily: "basic-sans",
            textDecoration: "none",
            fontSize: "1rem",
          }}
        >
          Edit My Profile
        </MuiLink>
        <Typography
          variant="body1"
          color={theme.colors.darkGray}
                sx={{
                  fontFamily: "basic-sans",
                  fontWeight: "700",
                  display: {
                    xs: "none",
                    sm: "block",
                    md: "block",
                    lg: "block",
                    xl: "block", // Fixed typo here
                  },
                }}
              >
                {" "}
                |{" "}
              </Typography>
              <MuiLink
                component={Link}
                to="/challenge/submission/contributions"
                sx={{
                  color: theme.colors.amber,
                  fontFamily: "basic-sans",
                  textDecoration: "none",
                  fontSize: "1rem",
                }}
              >
                View My Purchases
              </MuiLink>
              <Typography
          variant="body1"
          color={theme.colors.darkGray}
                sx={{
                  fontFamily: "basic-sans",
                  fontWeight: "700",
                  display: {
                    xs: "none",
                    sm: "block",
                    md: "block",
                    lg: "block",
                    xl: "block", // Fixed typo here
                  },
                }}
              >
                {" "}
                |{" "}
              </Typography>
              <MuiLink
                component={Link}
                to="/marketplace/creators"
                sx={{
                  color: theme.colors.amber,
                  fontFamily: "basic-sans",
                  textDecoration: "none",
                  fontSize: "1rem",
                }}
              >
                View My Dashboard
              </MuiLink>
            </Box>
          )}
  </Box>

 {/* Column 3: Upload Button */}
 <Box sx={{ mt: { xs: 2, sm: 0 }, textAlign: "center" }}>
    <Button
      variant="contained"
      onClick={handleClick}
      sx={{
        display: "flex",
        backgroundColor: theme.colors.blue,
        color: theme.colors.white,
        fontFamily: "basic-sans",
        fontWeight: "700",
        textTransform: "none",
        padding: "10px 20px",
        borderRadius: "50px",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: theme.colors.green,
          boxShadow: "none",
        },
      }}
    >
      <Typography
        variant="h6"
        component="h3"
        sx={{
          mr: 1,
          fontSize: {
            md: "1.05rem",
            xl: "1.7rem",
          },
          fontWeight: 500,
        }}
      >
        Challenges
      </Typography>
      <ForwardRoundedIcon
        sx={{
          fontSize: {
            xs: "2rem",
            sm: "1.4rem",
            md: "1.8rem",
            lg: "2.0rem",
          },
        }}
      />
    </Button>
  </Box>
</Box>


      {/* Second Row: User Bio */}
      <Box
        sx={{
          width: { xs: "100%", sm: "100%", md: "50%" },
          mt: 2,
          fontFamily: "basic-sans",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.colors.darkGray,
            fontFamily: "basic-sans",
          }}
        >
          {userProfile ? (userProfile?.bio || "No bio available.") : artistProfile?.biography}
        </Typography>
      </Box>

      {/* Third Row: Follow Button */}
      {userProfile &&
      !isCurrentUser && currentUser && (
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handleFollowToggle}
            sx={{
              color: "white",
              fontWeight: "700",
              backgroundColor: isFollowing
                ? theme.colors.red
                : theme.colors.green,
              borderRadius: "50px",
              textTransform: "none",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: isFollowing
                  ? theme.colors.amber
                  : theme.colors.blue,
              },
              boxShadow: "none",
            }}
          >
            {isFollowing ? `Unfollow ${firstName}` : `Follow ${firstName}`}
          </Button>
        </Box>
      )
    }
    </Box>
  );
};

export default ProfileHeader;
